import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  Paper,
  IconButton,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import Navbar from "../../../../globals/components/Navbar";
import { apiClient } from "../../../../globals/utils/api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import TablePaginationActions from "../../../../globals/components/TablePaginationActions";

function CarrierList() {
  const navigate = useNavigate();
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [carrierName, setCarrierName] = useState("");
  const [carrierAgency, setCarrierAgency] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [editOpen, setEditOpen] = useState(false);
  const [currentCarrier, setCurrentCarrier] = useState(null);

  const handleEditOpen = (carrier) => {
    setCurrentCarrier(carrier);
    setCarrierName(carrier.name);
    setEmail(carrier.carrier_processing_email);
    setCarrierAgency(carrier.agency_code);
    setEditOpen(true);
  };
  
  const handleEditClose = () => {
    setEditOpen(false);
    setCurrentCarrier(null);
    setCarrierName("");
    setEmail("");
    setCarrierAgency("");
  };
  
  const handleDelete = async (carrierId) => {
    setLoading(true);
    try {
      await apiClient(`/carriers/${carrierId}/`, "DELETE");
      toast.success("Carrier deleted successfully");
      fetchCarriers(); // Refresh the list
    } catch (error) {
      toast.error("Failed to delete carrier");
    } finally {
      setLoading(false);
    }
  };
  
  const handleEditSubmit = async () => {
    if (!currentCarrier) return;
  
    setLoading(true);
    try {
      const updatedCarrier = {
        name: carrierName,
        carrier_processing_email: email,
        agency_code: carrierAgency,
      };
      await apiClient(`/carriers/${currentCarrier.bond_carrier_id}/`, "PATCH", updatedCarrier);
      toast.success("Carrier updated successfully");
      fetchCarriers(); // Refresh the list
      handleEditClose();
    } catch (error) {
      toast.error("Failed to update carrier");
    } finally {
      setLoading(false);
    }
  };

  



  useEffect(() => {
    fetchCarriers();
  }, []);

  const fetchCarriers = async () => {
    setLoading(true);
    try {
      const data = await apiClient("/carriers/0", "GET");
      const carriersWithDefaults = data.data.map((carrier) => ({
        ...carrier,
        total_bonds_issued: carrier.total_bonds_issued || 0,
        total_outstanding_bond_amount: carrier.total_outstanding_bond_amount || "",
      }));
      setCarriers(carriersWithDefaults);
      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch carriers:", error);
      toast.error("Failed to load carriers");
      setLoading(false);
    }
  };

  const handleAddCarrier = async () => {
    setLoading(true);
    try {
      const newCarrier = {
        name: carrierName,
        carrier_processing_email: email,
        agency_code: carrierAgency,
      };
      await apiClient("/carriers/", "POST", newCarrier);
      toast.success("Carrier added successfully");
      fetchCarriers(); // Refresh the list after adding a carrier
      setOpen(false);
      setEmail("");
      setCarrierName("");
      setCarrierAgency("");
    } catch (error) {
      toast.error("Failed to add carrier");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredCarriers = carriers.filter((carrier) =>
    carrier.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const currentRows = filteredCarriers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleViewClick = (carrier) => {
    localStorage.setItem("carrierName", carrier.name)
    navigate(`/vermost_carrier_profile/${carrier.bond_carrier_id}`);
  }
  const handleNewClick = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const formatAmount = (amount) => {
    if (amount === undefined || amount === null) return '';
    return new Intl.NumberFormat('en-US').format(amount);
  };

  return (
    <>
      <Navbar />
      <ToastContainer />
      <Box sx={{ maxWidth: "100%", padding: "20px" }}>
        <Typography variant="h4" sx={{ fontWeight: "bold", mb: 2 }}>
          Bond Carriers
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "20px",
          }}
        >
          <TextField
            label="Search by Carrier Name"
            variant="outlined"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              width: "300px",
              borderRadius: "8px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleNewClick}
          >
            Add New Carrier
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{ width: "100%", overflowX: "auto", mb: 2, borderRadius: "8px" }}
          >
            <Table>
              <TableHead>
                <TableRow style={{ backgroundColor: "#f0f0f0" }}>
                  <TableCell>Carrier Name</TableCell>
                  <TableCell align="right">Carrier Processing Email Address</TableCell>
                  <TableCell align="right">Carrier Agency Code</TableCell>
                  <TableCell align="right">Total Bonds Issued</TableCell>
                  <TableCell align="right">Total Outstanding Bond Amount</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentRows.map((carrier) => (
                  <TableRow key={carrier.bond_carrier_id}>
                    <TableCell>{carrier.name || "N/A"}</TableCell>
                    <TableCell align="right">{carrier.carrier_processing_email || "N/A"}</TableCell>
                    <TableCell align="right">{carrier.agency_code || "N/A"}</TableCell>
                    <TableCell align="right">{carrier.total_bonds_issued || ""}</TableCell>
                    <TableCell align="right">${formatAmount(carrier.total_outstanding_bond_amount) || ""}</TableCell>
<TableCell align="center">
  <IconButton onClick={() => handleViewClick(carrier)} color="primary">
    <VisibilityIcon />
  </IconButton>
  <IconButton onClick={() => handleEditOpen(carrier)} color="secondary">
    <EditIcon />
  </IconButton>
  <IconButton onClick={() => handleDelete(carrier.bond_carrier_id)} color="error">
    <DeleteIcon />
  </IconButton>
</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <TablePagination
          ActionsComponent={TablePaginationActions}
          component="div"
          count={filteredCarriers.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Box>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Add New Carrier</DialogTitle>
        <DialogContent sx={{ width: "400px" }}>
          <TextField
            autoFocus
            margin="dense"
            label="Carrier Name"
            type="text"
            fullWidth
            variant="outlined"
            value={carrierName}
            onChange={(e) => setCarrierName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Carrier Processing Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Carrier Agency Code"
            type="carrierAgency"
            fullWidth
            variant="outlined"
            value={carrierAgency}
            onChange={(e) => setCarrierAgency(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleAddCarrier} color="primary" variant="contained">
            Add Carrier
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={handleEditClose}>
  <DialogTitle>Edit Carrier</DialogTitle>
  <DialogContent sx={{ width: "400px" }}>
    <TextField
      autoFocus
      margin="dense"
      label="Carrier Name"
      type="text"
      fullWidth
      variant="outlined"
      value={carrierName}
      onChange={(e) => setCarrierName(e.target.value)}
    />
    <TextField
      margin="dense"
      label="Carrier Processing Email Address"
      type="email"
      fullWidth
      variant="outlined"
      value={email}
      onChange={(e) => setEmail(e.target.value)}
    />
    <TextField
      margin="dense"
      label="Carrier Agency Code"
      type="carrierAgency"
      fullWidth
      variant="outlined"
      value={carrierAgency}
      onChange={(e) => setCarrierAgency(e.target.value)}
    />
  </DialogContent>
  <DialogActions>
    <Button onClick={handleEditClose} color="primary">
      Cancel
    </Button>
    <Button onClick={handleEditSubmit} color="primary" variant="contained">
      Save Changes
    </Button>
  </DialogActions>
</Dialog>


    </>
  );
}

export default CarrierList;
